import { Button } from "@/components/_components/button";
import toast from "react-hot-toast";

import { DialogClose } from "@/components/_components/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/_components/form";
import { Switch } from "@/components/_components/switch";
import { SUPPLEMENT_ADDITIONAL_ATTR } from "@/constants/constants";
import {
  getGetAllSupplementDataQueryKey,
  useCreateSupplementData,
  useUpdateSupplementData,
} from "@/lib/api/engageApi";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { cn } from "@/lib/utils";
import { ExtendedSupplementDataDto } from "../types/supplementDataTypes";
import AdditionalAttributesTable from "./AdditionalAttributesTable";
import { SupplementDataFormSchema, validateAdditionalAttributes } from "./schemas/supplementDataFormSchema";
import { Input } from "@/components/ui/input";
import { Loader2 } from "lucide-react";
import { useErrorHandler } from "@/hooks/useErrorHandler";

interface CreateSupplementDataFormProps {
  supplementData?: ExtendedSupplementDataDto;
  type?: string;
  onClose?: () => void;
}

const CreateSupplementDataForm = ({
  supplementData,
  type,
  onClose,
}: CreateSupplementDataFormProps) => {
  const queryClient = useQueryClient();
  const { handleError } = useErrorHandler();
  const onCloseRef = useRef(onClose);
  
  // Update ref when onClose changes
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  const {
    mutate: createMutate,
    error: createError,
    isSuccess: isCreateSuccess,
    isPending: isCreating,
  } = useCreateSupplementData();
  const {
    mutate: updateMutate,
    error: updateError,
    isSuccess: isUpdateSuccess,
    isPending: isUpdating,
  } = useUpdateSupplementData();
  
  const isSubmitting = isCreating || isUpdating;

  // Parse existing additional details
  const parseAdditionalDetails = (addDetails?: any) => {
    if (!addDetails) return {};
    if (typeof addDetails === 'string') {
      try {
        return JSON.parse(addDetails);
      } catch {
        return {};
      }
    }
    return addDetails;
  };

  const existingAddDetails = parseAdditionalDetails(supplementData?.addDetails);

  // Create dynamic initial values for additional attributes based on constants
  const createInitialAdditionalAttributes = () => {
    const initialValues: Record<string, string> = {};
    SUPPLEMENT_ADDITIONAL_ATTR.forEach((attr) => {
      initialValues[attr.key] = existingAddDetails[attr.key] || existingAddDetails[attr.label] || '';
    });
    return initialValues;
  };

  const form = useForm<z.infer<typeof SupplementDataFormSchema>>({
    resolver: zodResolver(SupplementDataFormSchema),
    defaultValues: {
      id: supplementData?.id ?? undefined,
      name: supplementData?.name ?? "",
      type: supplementData?.type ?? type ?? "",
      descr: supplementData?.descr ?? "",
      active: supplementData?.active ?? true,
      addDetails: typeof supplementData?.addDetails === 'string' 
        ? supplementData.addDetails 
        : Object.keys(existingAddDetails).length > 0 
          ? JSON.stringify(existingAddDetails) 
          : undefined,
      additionalAttributes: createInitialAdditionalAttributes(),
    },
  });

  const successfulMessage = supplementData
    ? `Master Data with name=${supplementData?.name} updated successfully!`
    : type
      ? `New ${type} created successfully!`
      : "Master Data created successfully!";

  const submitButtonLabel = supplementData ? "Update" : "Create";

  const handleAttributeChange = (key: string, value: string) => {
    // Update the form value
    form.setValue(`additionalAttributes.${key}` as any, value);
  };

  function onSubmit(values: z.infer<typeof SupplementDataFormSchema>) {
    // Validate additional attributes if they exist
    const selectedType = values.type;
    const relevantAttributes = SUPPLEMENT_ADDITIONAL_ATTR.filter(
      (attr) => attr.value === selectedType
    );

    if (relevantAttributes.length > 0) {
      // Get current form values for additional attributes
      const formAdditionalAttributes = form.getValues('additionalAttributes') || {};

      // Convert to Record<string, string> for validation
      const attributesForValidation: Record<string, string> = {};
      Object.entries(formAdditionalAttributes).forEach(([key, value]) => {
        attributesForValidation[key] = value || '';
      });

      // Use the validation function with current form values
      const validation = validateAdditionalAttributes(attributesForValidation, selectedType);

      if (!validation.isValid) {
        // Show all validation errors
        validation.errors.forEach((error) => {
          toast.error(error);
        });
        return;
      }

      // Format additional details as JSON string using keys instead of labels
      const addDetailsJson: Record<string, string> = {};
      relevantAttributes.forEach((attr) => {
        const value = formAdditionalAttributes[attr.key];
        if (value && value.trim()) {
          addDetailsJson[attr.key] = value.trim();
        }
      });

      // Convert to JSON string format for API submission
      values.addDetails = Object.keys(addDetailsJson).length > 0 ? JSON.stringify(addDetailsJson) : undefined;
    }

    // Remove additionalAttributes from values before sending to API
    const { additionalAttributes: _, ...submitValues } = values;

    if (supplementData) {
      updateMutate({ data: submitValues });
    } else {
      createMutate({ data: submitValues });
    }
  }

  useEffect(() => {
    if (isCreateSuccess) {
      queryClient.invalidateQueries({
        queryKey: getGetAllSupplementDataQueryKey(),
      });

      toast.success(successfulMessage);
      // Reset form only after successful creation
      form.reset();
      // Close the popup after successful creation
      onCloseRef.current?.();
    }
    if (createError) {
      handleError(createError);
    }
    if (isUpdateSuccess) {
      queryClient.invalidateQueries({
        queryKey: getGetAllSupplementDataQueryKey(),
      });

      toast.success(successfulMessage);
      // Close the popup after successful update
      onCloseRef.current?.();
    }
    if (updateError) {
      handleError(updateError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateSuccess, createError, isUpdateSuccess, updateError]);

  const isEditMode = Boolean(supplementData);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Name Field */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-foreground">
                Name <span className="text-destructive text-base font-bold">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  placeholder="Enter a name..."
                  disabled={isEditMode || isSubmitting}
                  className="h-10 bg-background border-border hover:border-primary/60 focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Description Field */}
        <FormField
          control={form.control}
          name="descr"
          render={({ field }) => (
            <FormItem>
              <FormLabel className="text-sm font-semibold text-foreground">
                Description
              </FormLabel>
              <FormControl>
                <textarea
                  placeholder="Enter a description..."
                  rows={4}
                  disabled={isSubmitting}
                  className={cn(
                    "flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm",
                    "shadow-sm transition-colors placeholder:text-muted-foreground",
                    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary",
                    "disabled:cursor-not-allowed disabled:opacity-50 resize-y",
                    "hover:border-primary/60"
                  )}
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Status Toggle */}
        <FormField
          control={form.control}
          name="active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border border-border/50 bg-card/50 p-4 shadow-sm transition-all hover:shadow-md">
              <div className="space-y-0.5">
                <FormLabel className="text-sm font-semibold text-foreground">
                  Status
                </FormLabel>
                <p className="text-xs text-muted-foreground">
                  {field.value ? "Active" : "Inactive"}
                </p>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  disabled={isSubmitting}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Additional Attributes Section */}
        {form.watch("type") && (
          <div className="pt-2">
            <AdditionalAttributesTable
              control={form.control}
              selectedType={form.watch("type")}
              onAttributeChange={handleAttributeChange}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-3 pt-4 border-t border-border/50">
          <DialogClose asChild>
            <Button 
              type="button"
              variant="outline" 
              disabled={isSubmitting}
              className="h-10 px-6 border-border hover:bg-accent transition-all"
            >
              Cancel
            </Button>
          </DialogClose>
          <Button 
            type="submit" 
            disabled={isSubmitting}
            className="h-10 px-6 bg-primary hover:bg-primary/90 text-white shadow-md hover:shadow-lg transition-all duration-200 font-semibold text-sm rounded-md"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {isCreating ? "Creating..." : "Updating..."}
              </>
            ) : (
              submitButtonLabel
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default CreateSupplementDataForm;